<script setup lang="ts">
import { requestBenchmarkAccess } from '@Shared/api/identity';
import { select, arc } from 'd3';
import { onMounted, ref } from 'vue';
import { useAppInsights } from '@Shared/utils/util';

const svgRef = ref<SVGElement | null>(null);

interface InactiveProgram {
  title: string;
  index: number;
  standardizedProgramName: string;
}

const props = defineProps<InactiveProgram>();

// Use benchmarkScore prop or fallback to random value
const benchmarkLabel = 'BENCHMARK';
const minValue = '-100';
const maxValue = '+100';
const benchmarkAccessRequested = ref(false);
const getInactiveButtonId = () => `inactive-program-gauge-button-${props.index}`;

const requestBenchmark = async (programName: string) => {
  if (benchmarkAccessRequested.value) return;
  await requestBenchmarkAccess(programName);
  useAppInsights().trackEvent({ name: `Benchmarking - Program Access - ${props.standardizedProgramName}` });
  benchmarkAccessRequested.value = true;
};

onMounted(() => {
  const svg = select(svgRef.value);

  // Draw the gauge once since we have static values
  svg.selectAll('*').remove();

  // Background arc (gray) - made even thinner
  const backgroundGauge = arc()
    .innerRadius(90)
    .outerRadius(100)
    .cornerRadius(10)
    .startAngle(-Math.PI / 2) as any;

  svg
    .append('path')
    .datum({
      endAngle: Math.PI / 2,
    })
    .attr('fill', '#e5e7eb')
    .attr('d', backgroundGauge);

  // Center score text (show dash for inactive)
  svg
    .append('text')
    .text('-')
    .attr('text-anchor', 'middle')
    .attr('transform', 'translate(0, -20)')
    .attr('font-size', '50')
    .attr('font-weight', 'bold')
    .attr('fill', '#9CA3AF');

  svg
    .append('text')
    .text(benchmarkLabel)
    .attr('text-anchor', 'middle')
    .attr('transform', 'translate(0, 5)')
    .attr('font-size', '13')
    .attr('fill', '#9CA3AF');

  // Min and max labels
  svg
    .append('text')
    .text(minValue)
    .attr('text-anchor', 'middle')
    .attr('transform', 'translate(-100, 25)')
    .attr('font-size', '12')
    .attr('font-weight', '400')
    .attr('fill', '#828282');

  svg
    .append('text')
    .text(maxValue)
    .attr('text-anchor', 'middle')
    .attr('transform', `translate(90, 25)`)
    .attr('font-size', '12')
    .attr('font-weight', '400')
    .attr('fill', '#828282');
});
</script>

<template>
  <div class="w-100% m-0 bg-white rounded-xl px-2 py-6">
    <!-- Title -->
    <div class="text-center text-2xl font-family[Poppins] font-bold text-gray-400">
      {{ title }}
    </div>

    <!-- Gauge SVG -->
    <svg viewBox="-130 -150 260 180">
      <g ref="svgRef" />
    </svg>

    <!-- Yellow Button -->
    <div class="text-center mt-2">
      <button
        @click="requestBenchmark(title)"
        class="bg-amber-200 hover:bg-amber-300 text-#4F4F4F px-4 py-0.9 rounded-lg font-medium text-sm transition-colors duration-200 shadow-sm"
        :id="getInactiveButtonId()"
      >
        <div v-if="benchmarkAccessRequested">
          <span>Noted!</span>
          <br />
          <span>Expect an update from us soon</span>
        </div>
        <div v-else>
          <span>Benchmark against the industry.</span>
          <br />
          <span class="underline">Click here to activate program.</span>
        </div>
      </button>
    </div>
  </div>
</template>
